/* eslint-disable react/forbid-prop-types */
/* eslint-disable react/no-unused-prop-types */
/* eslint-disable react/prop-types */
import { useState, useCallback } from "react";
import {
  Text,
  Card,
  Box,
  BlockStack,
  InlineGrid,
  InlineStack,
  Button,
  Banner,
  Badge,
} from "@shopify/polaris";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { API_URL } from "constants";
import {
  setModalxComponent,
  setModalxComponentProps,
} from "components/modalx/modalx.slice";
import Currency from "components/format/currency/currency";
import { getCountryName } from "constants/countries";

// A bare link is as tall as its text (about 16 px); on a phone it needs a 44 px tap target.
const contactLinkCls = "max-md:inline-flex max-md:min-h-11 max-md:items-center";

function CustomerDetails({ customer }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const contactPersonNode = customer.contactPerson ? (
    <Text as="p" variant="bodyMd">
      {customer.contactPerson}
    </Text>
  ) : (
    <Text as="p" tone="subdued">
      {t("no_contact_person_provided")}
    </Text>
  );
  const emailNode = customer.email ? (
    <Text as="p" variant="bodyMd">
      <a href={`mailto:${customer.email}`} className={contactLinkCls}>{customer.email}</a>
    </Text>
  ) : (
    <Text as="p" tone="subdued">
      {t("no_email_address_provided")}
    </Text>
  );
  const phoneNode = customer.phone ? (
    <Text as="p" variant="bodyMd">
      <a href={`tel:${customer.phone}`} className={contactLinkCls}>{customer.phone}</a>
    </Text>
  ) : (
    <Text as="p" tone="subdued">
      {t("no_phone_number_provided")}
    </Text>
  );
  // Build address display from separate fields
  const addressParts = [];
  if (customer.street) addressParts.push(customer.street);
  if (customer.houseNumber) addressParts.push(customer.houseNumber);
  if (customer.city) addressParts.push(customer.city);
  if (customer.region) addressParts.push(customer.region);
  if (customer.zipCodeLetters || customer.zipCodeFigures) {
    addressParts.push(
      `${customer.zipCodeLetters || ""}${customer.zipCodeFigures || ""}`
    );
  }
  if (customer.country) addressParts.push(getCountryName(customer.country, t));

  const hasAddressFields =
    customer.street ||
    customer.houseNumber ||
    customer.city ||
    customer.region ||
    customer.zipCodeLetters ||
    customer.zipCodeFigures ||
    customer.country;

  const addressNode = hasAddressFields ? (
    <BlockStack gap="100">
      {customer.street && (
        <Text as="p" variant="bodyMd">
          <strong>{t("street")}:</strong> {customer.street}
        </Text>
      )}
      {customer.houseNumber && (
        <Text as="p" variant="bodyMd">
          <strong>{t("house_number")}:</strong> {customer.houseNumber}
        </Text>
      )}
      {customer.city && (
        <Text as="p" variant="bodyMd">
          <strong>{t("city")}:</strong> {customer.city}
        </Text>
      )}
      {customer.region && (
        <Text as="p" variant="bodyMd">
          <strong>{t("region")}:</strong> {customer.region}
        </Text>
      )}
      {(customer.zipCodeLetters || customer.zipCodeFigures) && (
        <Text as="p" variant="bodyMd">
          <strong>{t("postal_code")}:</strong> {customer.zipCodeLetters || ""}
          {customer.zipCodeFigures || ""}
        </Text>
      )}
      {customer.country && (
        <Text as="p" variant="bodyMd">
          <strong>{t("country")}:</strong> {getCountryName(customer.country, t)}
        </Text>
      )}
    </BlockStack>
  ) : customer.address ? (
    <Text as="p" variant="bodyMd">
      {customer.address}
    </Text>
  ) : (
    <Text as="p" tone="subdued">
      {t("no_address_provided")}
    </Text>
  );
  const companyNameNode =
    customer.name || customer.legalName ? (
      <Text as="p" variant="bodyMd">
        {[customer.name, customer.legalName].filter(Boolean).join(" ")}
      </Text>
    ) : (
      <Text as="p" tone="subdued">
        {t("no_name_provided")}
      </Text>
    );
  const companyCodeNode = customer.companyCode ? (
    <Text as="p" variant="bodyMd">
      {customer.companyCode}
    </Text>
  ) : (
    <Text as="p" tone="subdued">
      {t("no_registration_code_provided")}
    </Text>
  );
  const vatCodeNode = customer.vatCode ? (
    <Text as="p" variant="bodyMd">
      {customer.vatCode}
    </Text>
  ) : (
    <Text as="p" tone="subdued">
      {t("no_vat_code_provided")}
    </Text>
  );

  const externalCodeNode = customer.externalCode ? (
    <Text as="p" variant="bodyMd">
      {customer.externalCode}
    </Text>
  ) : (
    <Text as="p" tone="subdued">
      {t("no_external_code_provided")}
    </Text>
  );

  const maxAllowedDebtNode = customer.maxAllowedDebt ? (
    <Text as="p" variant="bodyMd">
      {t("max_allowed_debt")}:{" "}
      <Currency isSymbol={true} value={customer.maxAllowedDebt} />
    </Text>
  ) : (
    <Text as="p" tone="subdued">
      {t("no_max_allowed_debt_provided")}
    </Text>
  );
  const debtNode = (
    <Text as="p" variant="bodyMd">
      {t("debtWithVat")}:{" "}
      <Currency isSymbol={true} value={customer._debt} isDecimal={true} />
    </Text>
  );
  const overdueDebtNode = (
    <Text as="p" variant="bodyMd">
      {t("overdue_debt")}:{" "}
      <Currency
        isSymbol={true}
        value={customer._overdueDebt}
        isDecimal={true}
      />
    </Text>
  );
  const assignedUserNode = customer.assignedUser ? (
    <Text as="p" variant="bodyMd">
      {t("assigned_user")}: {customer.assignedUser.name}
    </Text>
  ) : (
    <Text as="p" tone="subdued">
      {t("no_assigned_person")}
    </Text>
  );
  const turnoverNode = (
    <Text as="p" variant="bodyMd">
      {t("turnoverWithoutVat")}:{" "}
      <Currency isSymbol={true} isDecimal={true} value={customer._turnover} />
    </Text>
  );

  // A partner may settle through several accounts (EUR and USD, say). Customers
  // whose accounts predate the list still have only the legacy columns, so they
  // are shown as the single account they are.
  const bankAccounts =
    customer.bankAccounts?.length > 0
      ? [...customer.bankAccounts].sort((a, b) => {
          if (!!a.isPrimary !== !!b.isPrimary) {
            return a.isPrimary ? -1 : 1;
          }
          return (a.sortOrder || 0) - (b.sortOrder || 0);
        })
      : [
          {
            id: "legacy",
            iban: customer.bankIban,
            bankName: customer.bankName,
            swiftCode: customer.bankSwiftCode,
            isPrimary: true,
          },
        ].filter((account) => account.iban || account.bankName);

  const bankAccountsNode = bankAccounts.length ? (
    <BlockStack gap="300">
      {bankAccounts.map((account) => {
        const number = account.iban || account.accountNumber;
        const bankLine = [account.bankName, account.swiftCode]
          .filter(Boolean)
          .join(" · ");
        return (
          <BlockStack gap="050" key={account.id}>
            <InlineStack gap="200" blockAlign="center">
              <Text as="p" variant="bodyMd">
                {number || t("no_bank_iban_provided")}
              </Text>
              {bankAccounts.length > 1 && account.isPrimary && (
                <Badge tone="info">{t("bank_account_primary")}</Badge>
              )}
              {account.currency && <Badge>{account.currency}</Badge>}
            </InlineStack>
            {bankLine ? (
              <Text as="p" tone="subdued" variant="bodySm">
                {bankLine}
              </Text>
            ) : (
              <Text as="p" tone="subdued" variant="bodySm">
                {t("no_bank_name_provided")}
              </Text>
            )}
            {account.comment && (
              <Text as="p" tone="subdued" variant="bodySm">
                {account.comment}
              </Text>
            )}
          </BlockStack>
        );
      })}
    </BlockStack>
  ) : (
    <Text as="p" tone="subdued">
      {t("no_bank_iban_provided")}
    </Text>
  );
  const paymentDelayNode = customer.paymentDelay ? (
    <Text as="p" variant="bodyMd">
      {t("payment_delay")}: {customer.paymentDelay}
    </Text>
  ) : (
    <Text as="p" tone="subdued">
      {t("no_payment_delay_provided")}
    </Text>
  );
  const prepaymentPercentageNode = customer.prepaymentPercentage ? (
    <Text as="p" variant="bodyMd">
      {t("prepayment_percentage")}: {customer.prepaymentPercentage}%
    </Text>
  ) : (
    <Text as="p" tone="subdued">
      {t("no_prepayment_percentage_provided")}
    </Text>
  );
  const fineNode = customer.fine ? (
    <Text as="p" variant="bodyMd">
      {t("fine")}: {customer.fine}%
    </Text>
  ) : (
    <Text as="p" tone="subdued">
      {t("no_fine_provided")}
    </Text>
  );

  const invoiceEmailNode = customer.invoiceEmail ? (
    <Text as="p" variant="bodyMd">
      <a href={`mailto:${customer.invoiceEmail}`} className={contactLinkCls}>{customer.invoiceEmail}</a>
    </Text>
  ) : (
    <Text as="p" tone="subdued">
      {t("no_invoice_email_address_provided")}
    </Text>
  );

  const secondaryEmailNode = customer.secondaryEmail ? (
    <Text as="p" variant="bodyMd">
      <a href={`mailto:${customer.secondaryEmail}`} className={contactLinkCls}>
        {customer.secondaryEmail}
      </a>
    </Text>
  ) : (
    <Text as="p" tone="subdued">
      {t("no_secondary_email_address_provided")}
    </Text>
  );

  const [loginLink, setLoginLink] = useState(null);
  const [loginLinkLoading, setLoginLinkLoading] = useState(false);
  const [loginLinkCopied, setLoginLinkCopied] = useState(false);
  const [loginLinkError, setLoginLinkError] = useState(null);
  const [sendLinkLoading, setSendLinkLoading] = useState(false);
  const [sendLinkSuccess, setSendLinkSuccess] = useState(false);
  const [sendLinkError, setSendLinkError] = useState(null);

  const handleGenerateLoginLink = useCallback(async () => {
    setLoginLinkLoading(true);
    setLoginLink(null);
    setLoginLinkError(null);
    setLoginLinkCopied(false);
    try {
      const res = await axios.post(
        `${API_URL}/customer-portal/generate-login-link`,
        {
        customerId: customer.id,
        }
      );
      setLoginLink(res.data.loginUrl);
    } catch {
      setLoginLinkError(t("customer_portal_link_error"));
    } finally {
      setLoginLinkLoading(false);
    }
  }, [customer.id, t]);

  const handleCopyLoginLink = useCallback(() => {
    if (loginLink) {
      navigator.clipboard.writeText(loginLink);
      setLoginLinkCopied(true);
      setTimeout(() => setLoginLinkCopied(false), 3000);
    }
  }, [loginLink]);

  const handleSendLoginLink = useCallback(async () => {
    if (!customer.email) {
      setSendLinkError(t("customer_portal_email_missing"));
      return;
    }
    setSendLinkLoading(true);
    setSendLinkError(null);
    setSendLinkSuccess(false);
    try {
      await axios.post(`${API_URL}/customer-portal/send-login-link`, {
        email: customer.email,
      });
      setSendLinkSuccess(true);
      setTimeout(() => setSendLinkSuccess(false), 4000);
    } catch {
      setSendLinkError(t("customer_portal_send_link_error"));
    } finally {
      setSendLinkLoading(false);
    }
  }, [customer.email, t]);

  const handleCompanyDetailsEdit = () => {
    dispatch(setModalxComponent("CustomerForm"));
    dispatch(setModalxComponentProps({ id: customer.id }));
  };

  return (
    <Card roundedAbove="sm">
      <InlineGrid columns="1fr auto">
        <Text as="h2" variant="headingSm">
          {t("customer_details")}
        </Text>
        <Button
          variant="plain"
          onClick={handleCompanyDetailsEdit}
          accessibilityLabel="Preview"
        >
          {t("edit")}
        </Button>
      </InlineGrid>
      {companyNameNode}
      {companyCodeNode}
      {vatCodeNode}
      {externalCodeNode}
      <BlockStack gap="200">
        <Box paddingBlockStart="200">
          <Text as="h3" variant="headingSm">
            {t("contacts")}
          </Text>
          {contactPersonNode}
          {emailNode}
          {phoneNode}
        </Box>
        <Box paddingBlock="200">
          <Text as="h3" variant="headingSm">
            {t("address")}
          </Text>
          {addressNode}
        </Box>
        <Box paddingBlock="200">
          <Text as="h3" variant="headingSm">
            {t("bank_details")}
          </Text>
          {bankAccountsNode}
        </Box>
        <Box paddingBlock="200">
          <Text as="h3" variant="headingSm">
            {t("financials")}
          </Text>
          {/* {maxAllowedDebtNode} */}
          {overdueDebtNode}
          {debtNode}
          {turnoverNode}
          {/* {paymentDelayNode}
          {prepaymentPercentageNode}
          {fineNode} */}
          {assignedUserNode}
        </Box>
        <Box paddingBlock="200">
          <Text as="h3" variant="headingSm">
            {t("invoice")}
          </Text>
          {invoiceEmailNode}
          {secondaryEmailNode}
        </Box>
        <Box paddingBlock="200">
          <Text as="h3" variant="headingSm">
            {t("customer_portal")}
          </Text>
          <Box paddingBlockStart="200">
            <InlineStack gap="200">
              <Button
                onClick={handleGenerateLoginLink}
                loading={loginLinkLoading}
                size="slim"
              >
                {t("customer_portal_generate_link")}
              </Button>
              <Button
                onClick={handleSendLoginLink}
                loading={sendLinkLoading}
                size="slim"
              >
                {t("customer_portal_send_link")}
              </Button>
            </InlineStack>
          </Box>
          {sendLinkSuccess && (
            <Box paddingBlockStart="200">
              <Banner tone="success" onDismiss={() => setSendLinkSuccess(false)}>
                <Text as="p">{t("customer_portal_send_link_success")}</Text>
              </Banner>
            </Box>
          )}
          {sendLinkError && (
            <Box paddingBlockStart="200">
              <Banner tone="critical" onDismiss={() => setSendLinkError(null)}>
                <Text as="p">{sendLinkError}</Text>
              </Banner>
            </Box>
          )}
          {loginLink && (
            <Box paddingBlockStart="200">
              <Banner tone="success" onDismiss={() => setLoginLink(null)}>
                <BlockStack gap="200">
                  <Text as="p" variant="bodySm" breakWord>
                    {loginLink}
                  </Text>
                  <InlineStack gap="200">
                    <Button size="slim" onClick={handleCopyLoginLink}>
                      {loginLinkCopied
                        ? t("customer_portal_link_copied")
                        : t("customer_portal_copy_link")}
                    </Button>
                  </InlineStack>
                </BlockStack>
              </Banner>
            </Box>
          )}
          {loginLinkError && (
            <Box paddingBlockStart="200">
              <Banner tone="critical" onDismiss={() => setLoginLinkError(null)}>
                <Text as="p">{loginLinkError}</Text>
              </Banner>
            </Box>
          )}
        </Box>
      </BlockStack>
    </Card>
  );
}

export default CustomerDetails;
